import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useUserAnalytics } from '../../../hooks/useAnalytics';
import MetricCard from '../core/MetricCard';
import KPICard from '../core/KPICard';
import AreaChart from '../../ui/AreaChart';
import BarChart from '../../ui/BarChart';
import PieChart from '../../ui/PieChart';
import CollapsibleSection from '../../ui/CollapsibleSection';
import { transformToChartData, transformToChartDataAsync } from '../../../services/chartService';
import { getPreviousPeriodValue } from '../../../utils/chartHelpers';
import { formatPercent } from '../../../utils/format';
import { useFormatting } from '../../../hooks/useFormatting';
import { FILTER_PERIODS } from '../../../constants';
import './WalletMetrics.css';

/**
 * Wallet Metrics Component
 * Displays wallet-related analytics with thin, clean design
 * @param {Object} props - Component props
 * @param {string} props.period - Time period for metrics
 * @param {string|null} [props.platform] - Platform filter
 * @param {boolean} [props.compact] - Whether to display in compact mode
 */
const WalletMetrics = ({ period, platform = null, compact = false }) => {
  const { data: userData, loading, error } = useUserAnalytics(period, platform);
  const { formatNumber } = useFormatting();

  // Generate chart data for key metrics
  const [walletUsersChart, setWalletUsersChart] = useState(null);
  const [previousUserData, setPreviousUserData] = useState(null);

  // Fetch previous period data for accurate comparison
  useEffect(() => {
    const fetchPreviousPeriodData = async () => {
      try {
        const prevPeriodMap = {
          [FILTER_PERIODS.DAY]: FILTER_PERIODS.DAY,
          [FILTER_PERIODS.WEEK]: FILTER_PERIODS.WEEK,
          [FILTER_PERIODS.MONTH]: FILTER_PERIODS.MONTH,
          [FILTER_PERIODS.YEAR]: FILTER_PERIODS.YEAR,
          [FILTER_PERIODS.ALL]: FILTER_PERIODS.YEAR
        };
        
        const prevPeriod = prevPeriodMap[period] || FILTER_PERIODS.DAY;
        const { getUserAnalytics } = await import('../../../services/firebaseService');
        const prevUser = await getUserAnalytics(prevPeriod, platform).catch(() => null);
        setPreviousUserData(prevUser);
      } catch (error) {
        setPreviousUserData(null);
      }
    };

    if (userData) {
      fetchPreviousPeriodData();
    }
  }, [period, platform, userData]);

  useEffect(() => {
    if (!userData) {
      setWalletUsersChart(null);
      return;
    }

    const currentWalletUsers = userData.walletUsers || 0;
    const previousWalletUsers = getPreviousPeriodValue(userData, previousUserData, 'walletUsers', 0.9);
    
    Promise.all([
      transformToChartDataAsync(
        { walletUsers: currentWalletUsers },
        { walletUsers: previousWalletUsers },
        'walletUsers',
        period,
        {
          collectionName: 'users',
          // Check wallet_address (snake_case primary) first, then walletAddress (fallback)
          valueExtractor: (docData) => (docData.wallet_address || docData.walletAddress) ? 1 : 0
        }
      )
    ]).then(([walletChart]) => {
      setWalletUsersChart(walletChart);
    }).catch(() => {
      setWalletUsersChart(transformToChartData(
        { walletUsers: currentWalletUsers },
        { walletUsers: previousWalletUsers },
        'walletUsers',
        period
      ));
    });
  }, [userData, previousUserData, period]);

  // Normalize data for safe access - ensure all values are numbers
  const safeNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const normalized = {
    totalUsers: safeNumber(userData?.totalUserCount),
    walletUsers: safeNumber(userData?.walletUsers),
    healthyWallets: safeNumber(userData?.healthyWallets),
    activeUsers: safeNumber(userData?.activeUsers),
    verifiedUsers: safeNumber(userData?.verifiedUsers),
    onboardedUsers: safeNumber(userData?.onboardedUsers),
    migrationCompleted: safeNumber(userData?.migrationCompleted)
  };

  // Calculate percentages
  const walletAdoptionRate = normalized.totalUsers > 0 
    ? (normalized.walletUsers / normalized.totalUsers) * 100 
    : 0;
  const walletHealthRate = normalized.walletUsers > 0 
    ? (normalized.healthyWallets / normalized.walletUsers) * 100 
    : 0;
  const activeRate = normalized.walletUsers > 0 
    ? (normalized.activeUsers / normalized.walletUsers) * 100 
    : 0;
  const inactiveWallets = normalized.walletUsers - normalized.activeUsers;

  // Memoize chart data
  const walletAdoptionChartData = useMemo(() => {
    if (normalized.totalUsers === 0) return null;
    return [
      { label: 'With Wallets', value: normalized.walletUsers },
      { label: 'Without Wallets', value: normalized.totalUsers - normalized.walletUsers }
    ].filter(item => item.value > 0);
  }, [normalized.totalUsers, normalized.walletUsers]);

  const walletHealthChartData = useMemo(() => {
    if (normalized.walletUsers === 0) return null;
    return [
      { label: 'Healthy', value: normalized.healthyWallets },
      { label: 'Unhealthy', value: normalized.walletUsers - normalized.healthyWallets }
    ].filter(item => item.value > 0);
  }, [normalized.walletUsers, normalized.healthyWallets]);

  // Error state
  if (error) {
    return (
      <div className="wallet-metrics-enhanced error">
        <h3>Error loading wallet metrics</h3>
        <p>Please try refreshing the page</p>
      </div>
    );
  }

  return (
    <div className="wallet-metrics-enhanced">
      {/* Hero Section - Key KPIs */}
      <section className="wallet-hero">
        <div className="wallet-hero-header">
          <h1 className="wallet-title">
            <i className="bi bi-wallet2 icon-brand"></i>
            Wallet Analytics
          </h1>
          <p className="wallet-subtitle">{period} period{platform ? ` • ${platform}` : ''}</p>
        </div>
        
        <div className="wallet-kpi-grid">
          <KPICard
            title="Users with Wallets"
            value={normalized.walletUsers}
            formatType="number"
            icon="bi-wallet2"
            color="blue"
            subtitle={formatPercent(walletAdoptionRate, 1) + ' adoption'}
            loading={loading}
          />
          <KPICard
            title="Healthy Wallets"
            value={normalized.healthyWallets}
            formatType="number"
            icon="bi-patch-check"
            color="green"
            subtitle={formatPercent(walletHealthRate, 1) + ' healthy'}
            loading={loading}
          />
          <KPICard
            title="Active Wallets"
            value={normalized.activeUsers}
            formatType="number"
            icon="bi-check-circle"
            color="teal"
            subtitle={formatPercent(activeRate, 1) + ' active'}
            loading={loading}
          />
        </div>
      </section>

      {/* Wallet Status Overview */}
      <section className="wallet-section status-section">
        <div className="section-header">
          <h2 className="section-title">
            <i className="bi bi-diagram-3 icon-brand"></i>
            Wallet Status Overview
          </h2>
        </div>

        <div className="wallet-status-grid">
          <div className="status-card active-card">
            <div className="status-card-header">
              <i className="bi bi-check-circle status-icon"></i>
              <h3>Active Wallets</h3>
            </div>
            <div className="status-card-content">
              <div className="status-stat">
                <span className="status-stat-label">Count</span>
                <span className="status-stat-value">{formatNumber(normalized.activeUsers)}</span>
              </div>
              <div className="status-stat-mini">
                <span className="status-stat-mini-label">Percentage</span>
                <span className="status-stat-mini-value">{formatPercent(activeRate, 1)}</span>
              </div>
            </div>
          </div>

          <div className="status-card inactive-card">
            <div className="status-card-header">
              <i className="bi bi-x-circle status-icon"></i>
              <h3>Inactive Wallets</h3>
            </div>
            <div className="status-card-content">
              <div className="status-stat">
                <span className="status-stat-label">Count</span>
                <span className="status-stat-value">{formatNumber(inactiveWallets)}</span>
              </div>
              <div className="status-stat-mini">
                <span className="status-stat-mini-label">Percentage</span>
                <span className="status-stat-mini-value">{formatPercent(100 - activeRate, 1)}</span>
              </div>
            </div>
          </div>

          <div className="status-card healthy-card">
            <div className="status-card-header">
              <i className="bi bi-heart-fill status-icon"></i>
              <h3>Wallet Health</h3>
            </div>
            <div className="status-card-content">
              <div className="status-stat">
                <span className="status-stat-label">Healthy</span>
                <span className="status-stat-value">{formatNumber(normalized.healthyWallets)}</span>
              </div>
              <div className="status-stat-mini">
                <span className="status-stat-mini-label">Health Rate</span>
                <span className="status-stat-mini-value">{formatPercent(walletHealthRate, 1)}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Charts Section */}
      {walletAdoptionChartData && (
        <section className="wallet-section charts-section">
          <div className="section-header">
            <h2 className="section-title">
              <i className="bi bi-graph-up icon-brand"></i>
              Wallet Trends
            </h2>
          </div>
          
          <div className="charts-grid">
            {walletUsersChart && (
              <div className="chart-card">
                <h3 className="chart-title">Users with Wallets Over Time</h3>
                <AreaChart
                  data={walletUsersChart.data}
                  comparisonData={walletUsersChart.comparisonData}
                  label="Wallet Users"
                  formatType="number"
                  color="blue"
                />
              </div>
            )}
            
            {walletAdoptionChartData && (
              <div className="chart-card">
                <h3 className="chart-title">Wallet Adoption</h3>
                <BarChart
                  data={walletAdoptionChartData}
                  label="Wallet Adoption"
                  formatType="number"
                  orientation="vertical"
                />
              </div>
            )}
            
            {walletHealthChartData && (
              <div className="chart-card">
                <h3 className="chart-title">Wallet Health Status</h3>
                <PieChart
                  data={walletHealthChartData}
                  label="Wallet Health"
                  formatType="number"
                  showPercentages={true}
                  innerRadius={0.5}
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Additional Metrics - Collapsible */}
      <CollapsibleSection title="Additional Wallet Metrics" defaultExpanded={false}>
        <div className="metrics-grid-wrapper">
          <div className="metrics-grid">
            {/* User Engagement */}
            <div className="metric-group">
              <h3 className="group-title">User Engagement</h3>
              <div className="metric-cards">
                <MetricCard
                  title="Verified Users"
                  value={normalized.verifiedUsers}
                  formatType="number"
                  loading={loading}
                  icon={<i className="bi bi-shield-lock icon-brand"></i>}
                  color="blue"
                  subtitle={formatPercent((normalized.verifiedUsers / normalized.totalUsers) * 100 || 0, 1)}
                />
                <MetricCard
                  title="Onboarded Users"
                  value={normalized.onboardedUsers}
                  formatType="number"
                  loading={loading}
                  icon={<i className="bi bi-rocket-takeoff icon-brand"></i>}
                  color="green"
                  subtitle={formatPercent((normalized.onboardedUsers / normalized.totalUsers) * 100 || 0, 1)}
                />
                <MetricCard
                  title="Migration Complete"
                  value={normalized.migrationCompleted}
                  formatType="number"
                  loading={loading}
                  icon={<i className="bi bi-arrow-repeat icon-brand"></i>}
                  color="purple"
                  subtitle={formatPercent((normalized.migrationCompleted / normalized.totalUsers) * 100 || 0, 1)}
                />
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

WalletMetrics.propTypes = {
  period: PropTypes.string.isRequired,
  platform: PropTypes.string,
  compact: PropTypes.bool
};

WalletMetrics.defaultProps = {
  platform: null,
  compact: false
};

export default WalletMetrics;

